import {
  createEntityAdapter,
  EntityState,
  createSelector,
  configureStore,
} from "@reduxjs/toolkit";
import { museApi } from "../api/museApi";
import { Family_Entity } from "../../entities/family";

const familiesAdapter = createEntityAdapter<Family_Entity>();
const initialState = familiesAdapter.getInitialState();

export const familyEndpoints = museApi.injectEndpoints({
  endpoints: (build) => ({
    getFamilies: build.query<EntityState<Family_Entity>, void>({
      query: () => `families`,
      transformResponse: (responseData: Family_Entity[]) => {
        return familiesAdapter.setAll(initialState, responseData);
      },
      providesTags: ["families"],
    }),
    getFamily: build.query<Family_Entity, number>({
      query: (id) => `families/${id}`,
      providesTags: (result, error, id) => [{ type: "families", id }],
    }),
    createFamily: build.mutation<Family_Entity, Partial<Family_Entity>>({
      query: (body) => ({
        method: "POST",
        body,
        url: `families`,
      }),
      invalidatesTags: ["families"],
    }),
    updateFamily: build.mutation<
      void,
      { id: number; body: Partial<Family_Entity> }
    >({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `families/${args.id}`,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "families", id },
        "families",
      ],
    }),
    deleteFamily: build.mutation<void, number>({
      query: (id) => ({
        method: "DELETE",
        url: `families/${id}`,
      }),
      invalidatesTags: ["families"],
    }),
  }),
});

export const {
  useGetFamilyQuery,
  useGetFamiliesQuery,
  useCreateFamilyMutation,
  useDeleteFamilyMutation,
  useUpdateFamilyMutation,
} = familyEndpoints;

export default familyEndpoints;

export const selectFamiliesResult =
  familyEndpoints.endpoints.getFamilies.select();

const selectFamiliesData = createSelector(
  selectFamiliesResult,
  (familiesResult) => familiesResult.data
);

const store = configureStore({
  reducer: {
    [museApi.reducerPath]: museApi.reducer,
  },
});

type RootState = ReturnType<typeof store.getState>;

export const { selectAll: selectAllFamilies, selectById: selectFamilyById } =
  familiesAdapter.getSelectors<RootState>(
    (state) => selectFamiliesData(state) ?? initialState
  );
